<template>
  <InfoCard
    class="base-info-container"
    icon="iconfont iconcandidate"
    :title="$t(`${i18nPrefix}.title.baseInfo`)"
    :infoList="infoList"
    :infoLines="infoLines"
  >
    <template #referralChain>
      <ReferralChain :isPotential="isPotential" :referralChainInfo="referralChainInfo"/>
    </template>
    <template #chatTools>
      <div v-for="(chatTool, index) in chatTools" :key="index">
          <div class="content-name">
            <div class="content-name-label">{{ chatTool.label }}</div>
            <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="chatTool.value">
              <div class="content-name-value">{{ chatTool.value }}</div>
            </el-tooltip>
          </div>
      </div>
    </template>
    <template #socialNetworks>
      <div v-for="(network, index) in socialNetworks" :key="index">
          <div class="content-name">
            <div class="content-name-label">{{ network.label }}</div>
            <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="network.value">
              <div class="content-name-value">{{ network.value }}</div>
            </el-tooltip>
          </div>
      </div>
    </template>
    <template #resumePath>
      <span class="link" @click="handleResumePathClick">{{ $t(`${i18nPrefix}.label.resumePath`) }}</span>
    </template>
    <template #ashbyCandidateProfileUrl>
      <span class="link" @click="handleAshbyCandidateProfileUrlClick">{{ $t(`${i18nPrefix}.label.viewAshbyCandidateProfile`) }}</span>
    </template>
  </InfoCard>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import ReferralChain from './referralChain.vue'
import InfoCard from '@/components/candidate/infoCard.vue'
import { getI18nPropertyValue, getConstantI18nValue } from '@/i18n'
import { CHAT_TOOL, SOCIAL_NETWORK } from '@/constant/meta'
import { formatBigNumber } from '@/components/filters'
import { ROOT_COMPANY_ID } from '@/constant/company'

export default {
  components: {
    InfoCard,
    ReferralChain
  },
  props: {
    candidateInfo: {
      required: true
    },
    candidateBaseInfo: {
      required: true
    },
    status: {
      type: Object
    },
    isPotential: {
      type: Boolean,
      default: false
    },
    privacy: {
      type: String
    },
    hiddenFieldList: {
      type: Array,
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      getI18nPropertyValue
    }
  },
  computed: {
    infoList () {
      return [this.candidateBaseInfo]
    },
    infoLines () {
      const vm = this
      const lines = {
        applyTime: {
          labelI18nKey: `${this.i18nPrefix}.label.appliedDate`,
          multipleText: true,
          formatter: this.formatApplyTime,
          hidden: _.includes(vm.hiddenFieldList, 'applyTime')
        },
        candidateId: {
          labelI18nKey: `${this.i18nPrefix}.label.candidateId`,
          dataKey: 'candidate_id'
        },
        salutation: {
          labelI18nKey: `${this.i18nPrefix}.label.salutation`,
          formatter: (val) => {
            const i18nKey = _.findKey({ Mr: 'MR', Mrs: 'MRS', Ms: 'MS' }, (value) => value === val)
            return i18nKey && vm.$t(`${this.i18nPrefix}.label.${i18nKey}`)
          }
        },
        employeeId: {
          hidden: !this.candidateBaseInfo.employee?.ats_employee_id,
          labelI18nKey: `${this.i18nPrefix}.label.employeeId`,
          dataKey: 'employee.ats_employee_id'
        },
        gender: {
          labelI18nKey: `${this.i18nPrefix}.label.gender`,
          formatter: (val) => {
            const i18nKey = _.findKey({ male: 'M', female: 'F', nonBinary: 'N', preferNotToDisclose: 'P' }, (value) => value === val)
            return i18nKey && vm.$t(`common.gender.${i18nKey}`)
          }
        },
        birthday: {
          labelI18nKey: `${this.i18nPrefix}.label.birthday`,
          formatter: (val, info) => {
            const birthday = moment(val).format('YYYY/MM/DD')
            return val && `${birthday} (${info.yearsOld})`
          }
        },
        expectedSalary: {
          labelI18nKey: `${this.i18nPrefix}.label.expectedSalary`,
          formatter: (val, info) => {
            const currency = info.currency || ''
            const expectedSalary = formatBigNumber(getI18nPropertyValue(info.expected_salary, 'cn_name', 'name')) || ''
            return _.chain([expectedSalary, currency]).filter().join(' ')
          }
        },
        hukou: {
          label: this.$t(`${this.i18nPrefix}.label.homeLocation`),
          formatter: (val, info) => this.formatLocation(info.hukou_location, info)
        },
        location: {
          label: this.$t(`${this.i18nPrefix}.label.currentLocation`),
          formatter: (val, info) => this.formatLocation(info.current_location, info)
        },
        address: {
          label: this.$t(`${this.i18nPrefix}.label.address`),
          dataKey: 'address',
          formatter: (val, info) => vm.candidateBaseInfo.is_agency_uploaded_and_hid ? '***' : val
        },
        otherAddress: {
          label: this.$t(`${this.i18nPrefix}.label.otherAddress`),
          dataKey: 'other_address',
          formatter: (val, info) => vm.candidateBaseInfo.is_agency_uploaded_and_hid ? '***' : val
        },
        mobile: {
          labelI18nKey: `${this.i18nPrefix}.label.mobile`,
          formatter: (val, info) => vm.candidateBaseInfo.is_agency_uploaded_and_hid ? '***' : (info.mobile || val)
        },
        secondPhone: {
          labelI18nKey: `${this.i18nPrefix}.label.secondPhone`,
          dataKey: 'second_phone',
          formatter: (val, info) => vm.candidateBaseInfo.is_agency_uploaded_and_hid ? '***' : val
        },
        chatTools: {
          labelI18nKey: `${this.i18nPrefix}.label.chatId`,
          contentName: 'chatTools',
          hidden: vm.candidateBaseInfo.is_agency_uploaded_and_hid
        },
        socialNetworks: {
          labelI18nKey: `${this.i18nPrefix}.label.socialNetwork`,
          contentName: 'socialNetworks',
          hidden: vm.candidateBaseInfo.is_agency_uploaded_and_hid
        },
        expectedAddress: {
          labelI18nKey: `${this.i18nPrefix}.label.expectedAddress`,
          formatter: (val, info) => this.formatLocation(info.expected_job_location, info)
        },
        sourceChannel: {
          hidden: _.includes(vm.hiddenFieldList, 'sourceChannel'),
          label: this.$t(`${this.i18nPrefix}.label.applicationChannel`),
          formatter: (val, info) => {
            let channelName = info.source_channel ? this.getI18nPropertyValue(info.source_channel, 'cn_name', 'name') : this.$t('admin.application.applicantList.label.weChatChannelName')
            if (info.agency_note && info.tagged_source.source === 'Hiring_Agency') {
              channelName = `${channelName} ${info.agency_note}`
            }
            return channelName
          }
        },
        jobApplicationPlat: {
          hidden: _.includes(vm.hiddenFieldList, 'jobApplicationPlat'),
          label: this.$t(`${this.i18nPrefix}.label.jobApplicationPlat`),
          formatter: this.formatApplicationPlatForm
        },
        onboardDate: {
          hidden: _.includes(vm.hiddenFieldList, 'onboardDate'),
          label: this.$t(`${this.i18nPrefix}.label.onboardDate`),
          formatter: (val, info) => info.onboard_date && moment(info.onboard_date).format('YYYY/MM/DD')
        },
        hiredDate: {
          hidden: _.includes(vm.hiddenFieldList, 'hiredDate'),
          label: this.$t(`${this.i18nPrefix}.label.hiredDate`),
          formatter: (val, info) => info.hired_date && moment(info.hired_date).format('YYYY/MM/DD')
        },
        idNumber: {
          hidden: !this.isElcCompany || _.includes(vm.hiddenFieldList, 'idNumber'),
          dataKey: 'id_number',
          label: this.$t(`${this.i18nPrefix}.label.idNumber`)
        },
        referralInfo: {
          hidden: vm.candidateBaseInfo.is_agency_uploaded_and_hid || _.includes(vm.hiddenFieldList, 'referralInfo'),
          label: this.$t(`${this.i18nPrefix}.label.referrer`),
          dataKey: 'referral_info',
          multipleText: true,
          formatter: (val, info) => {
            if (!val?.name) {
              return
            }
            const referral = [
              val.name || '-',
              this.$t(`${this.i18nPrefix}.label.${val.employee ? 'employee' : 'nonEmployee'}`),
              val.employee ? val.employee.ats_employee_id : '',
              val.mobile_id || this.$t(`${this.i18nPrefix}.label.noMobile`),
              val.email || this.$t(`${this.i18nPrefix}.label.noEmail`),
              val.referral_reason
            ]
            return _.filter(referral).join('\n')
          }
        },
        referralChain: {
          contentName: 'referralChain',
          get hidden () {
            return vm.candidateBaseInfo.is_agency_uploaded_and_hid || !vm.candidateBaseInfo?.referral_info?.referral_chain || _.includes(vm.hiddenFieldList, 'referralChain')
          },
          label: this.$t(`${this.i18nPrefix}.label.referralChain`)
        },
        resumePath: {
          labelI18nKey: `${this.i18nPrefix}.label.resumePath`,
          get hidden () {
            return !['pdf', 'image', 'html', 'docx', 'doc'].includes(vm.candidateBaseInfo.resume_file_type)
          },
          contentName: 'resumePath'
        },
        // ashby 平台对接的candidate profile url
        ashbyCandidateProfileUrl: {
          labelI18nKey: `${this.i18nPrefix}.label.ashbyCandidateProfileUrl`,
          get hidden () {
            return !vm.candidateBaseInfo.ashby_candidate_profile_url
          },
          contentName: 'ashbyCandidateProfileUrl'
        }
      }
      return lines
    },
    isElcCompany () {
      return this.hasAnyPermission('COMPUTED_COMPANY_IS_ELC')
    },
    employeeReviewStatus () {
      const employee = this.candidateBaseInfo.referral_info.employee
      if (employee.status === '1') {
        return this.$t(`${this.i18nPrefix}.label.referralStatusPending`)
      } else if (employee.status === '2') {
        return this.$t(`${this.i18nPrefix}.label.referralStatusApproved`)
      } else if (employee.status === '3') {
        return this.$t(`${this.i18nPrefix}.label.referralStatusRejected`)
      }
      return ''
    },
    referralChainInfo () {
      const chainList = this.candidateBaseInfo.referral_info.referral_chain
      const reversed = []
      _.forEachRight(chainList, chain => {
        reversed.push(chain)
      })
      return reversed
    },
    chatTools () {
      const chatTools = []
      _.forEach(this.candidateBaseInfo.chat_tools, (accountList, tool) => {
        let toolI18n = getConstantI18nValue(CHAT_TOOL, tool, 'meta.CHAT_TOOL')
        if (toolI18n === 'null') {
          toolI18n = null
        }
        _.forEach(accountList, account => {
          chatTools.push({ label: toolI18n, value: account })
        })
      })
      return chatTools
    },
    socialNetworks () {
      const socialNetWorks = []
      _.forEach(this.candidateBaseInfo.social_net_works, (accountList, netWork) => {
        let netWorkI18n = getConstantI18nValue(SOCIAL_NETWORK, netWork, 'meta.SOCIAL_NETWORK')
        if (netWorkI18n === 'null') {
          netWorkI18n = null
        }
        _.forEach(accountList, account => {
          socialNetWorks.push({ label: netWorkI18n, value: account })
        })
      })
      return socialNetWorks
    }
  },
  created () {

  },
  methods: {
    formatLocation (location, info) {
      if (!location) {
        return
      }
      return _.chain(['country', 'province', 'city']).map(key => {
        const val = _.get(location, key)
        if (!val) {
          return
        }
        return getI18nPropertyValue(val, 'cn_name', 'name')
      }).filter().reverse().join(' ').value()
    },
    formatApplyTime (val, info) {
      const appliedDateList = []
      _.forEach(info.all_apply_job_dates, (date, index) => {
        const appliedDate = moment(date).format('YYYY/MM/DD')
        let duration = moment().diff(moment(appliedDate), 'days')
        const i18nPrefix = this.i18nPrefix
        if (duration < 1) {
          duration = moment().diff(moment(date), 'hours')
          const appliedHours = `(${this.$t(`${i18nPrefix}.label.appliedHours`, { duration: duration < 1 ? 1 : duration })})`
          const appliedTimes = `${this.$t(`${i18nPrefix}.label.appliedTimes`, { times: index + 1 })}`
          appliedDateList.push(`${appliedDate} ${appliedHours}\n${appliedTimes}`)
        } else {
          duration = duration > 99 ? '99+' : duration
          const appliedDays = `(${this.$t(`${i18nPrefix}.label.appliedDays`, { duration })})`
          const appliedTimes = `${this.$t(`${i18nPrefix}.label.appliedTimes`, { times: index + 1 })}`
          appliedDateList.push(`${appliedDate} ${appliedDays}\n${appliedTimes}`)
        }
      })
      return appliedDateList.join(' ')
    },
    formatApplicationPlatForm (val, info) {
      const source = this.candidateBaseInfo.tagged_source?.source
      const code = this.candidateBaseInfo.tagged_source?.code.trim()
      let text = null
      if (source === 'email_resume') {
        const emailResume = this.$t(`${this.i18nPrefix}.jobApplicationPlat.emailResume`)
        if (code && emailResume[code]) {
          text = emailResume[code]
        } else {
          if (process.env.VUE_APP_ENV === 'production') {
            text = this.$t(`${this.i18nPrefix}.jobApplicationPlat.ajinga`)
          } else {
            // eslint-disable-next-line no-console
            console.warn('this emailResume has no i18n text for code: ', code)
          }
        }
      } else if (source === 'CANDIDATE_UPLOAD') {
        text = this.$t(`${this.i18nPrefix}.jobApplicationPlat.candidateUpload`)
      } else if (source === 'Hiring_Agency') {
        text = this.$t(`${this.i18nPrefix}.jobApplicationPlat.uploadByAgency`)
      } else if (source === 'ajinga_RPO_lite') {
        text = this.$t(`${this.i18nPrefix}.jobApplicationPlat.aspireProgram`)
      } else if (source === 'Referral') {
        text = this.$t(`${this.i18nPrefix}.jobApplicationPlat.submittedReferral`)
      } else if ((this.candidateBaseInfo.company.root_company_id === ROOT_COMPANY_ID.MSD || this.candidateBaseInfo.company.root_company_id === ROOT_COMPANY_ID.MSD_OLD || this.candidateBaseInfo.company.root_company_id === ROOT_COMPANY_ID.SANOFI) &&
        ((source === 'Internal' && code === 'Current_Worker') || (source === 'Current Temporary / Contingent Worker' && code === 'Current Temporary / Contingent Worker') || (source === 'Internal_contingent' && code === 'Contingent_worker') || (source === 'Internal_employee' && code === 'Employee'))) {
        // MSD 定制的逻辑
        text = this.$t(`${this.i18nPrefix}.jobApplicationPlat.workday`)
      } else {
        text = this.$t(`${this.i18nPrefix}.jobApplicationPlat.ajinga`)
      }
      return text
    },
    handleResumePathClick () {
      this.$emit('viewOriginResume')
    },
    handleAshbyCandidateProfileUrlClick () {
      window.open(this.candidateBaseInfo.ashby_candidate_profile_url)
    }
  }
}
</script>

<style lang="scss" scoped>
.base-info-container {
  .content-name {
    width: 100%;
    white-space: nowrap;

    .content-name-label {
      display: inline-block;
      vertical-align: middle;
      width: 35%;
    }

    .content-name-value {
      display: inline-block;
      vertical-align: middle;
      width: 65%;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}
</style>
